/**
 * @fileoverview Conflict-check entity picker (MOR-1419)
 *
 * Purpose: choose the entities one conflict check covers. WLPR is often
 * appointed to a head company and several subsidiaries at once and wanted
 * one conflict email across the group rather than one per entity. The
 * check always covers the job it is run from; this picker adds the rest —
 * either another job on the pipeline (its ASIC/PPSR searches then feed the
 * brief) or, for a company that isn't on the platform yet, a name typed by
 * hand. With nothing added the check is the ordinary single-entity one.
 *
 * Used by: src/components/pre-appointment/pipeline-job-panel.tsx
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Building2, X } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { cn } from "@/utils/misc";
import {
  addEntityDraft,
  describePipelineJob,
  type ConflictEntityDraft,
} from "./conflict-entity-picker.helpers";

interface ConflictEntityPickerProps {
  orgId: Id<"organizations">;
  /** The job the check is run from — always covered, never removable. */
  jobId: Id<"jobs">;
  anchorName: string;
  entities: ConflictEntityDraft[];
  onChange: (entities: ConflictEntityDraft[]) => void;
  className?: string;
}

/**
 * @component
 * @example
 * <ConflictEntityPicker
 *   orgId={orgId}
 *   jobId={jobId}
 *   anchorName={job.entityName}
 *   entities={entities}
 *   onChange={setEntities}
 * />
 */
export function ConflictEntityPicker({
  orgId,
  jobId,
  anchorName,
  entities,
  onChange,
  className,
}: ConflictEntityPickerProps) {
  // The rest of the pipeline: the sibling jobs a group appointment is made
  // up of. Jobs already under an open check stay out — the backend refuses
  // them, and offering them would only invite the error.
  const { data: pipelineJobs } = useQuery(
    convexQuery(api.preAppointment.pipeline.listPipelineJobs, { orgId }),
  );
  const options = useMemo(() => {
    const listed = new Set(
      entities.flatMap((entity) => (entity.jobId ? [entity.jobId] : [])),
    );
    return (pipelineJobs ?? [])
      .filter(
        (job) =>
          job._id !== jobId &&
          !listed.has(job._id) &&
          job.openConflictCheckCount === 0,
      )
      .map((job) => {
        // A firm can hold several pitches for the same company: each row
        // carries its code (or reference and date) so they can be told
        // apart and searched by it.
        const { context, keywords } = describePipelineJob(job);
        return {
          value: String(job._id),
          label: job.entityName,
          description: context,
          keywords,
          title: `${job.entityName} · ${context}`,
        };
      });
  }, [pipelineJobs, entities, jobId]);

  const addJob = (value: string) => {
    const job = (pipelineJobs ?? []).find((j) => String(j._id) === value);
    if (!job) return;
    onChange(
      addEntityDraft(
        entities,
        {
          jobId: job._id,
          name: job.entityName,
          context: describePipelineJob(job).context,
        },
        anchorName,
      ),
    );
  };

  const addName = (name: string) => {
    onChange(addEntityDraft(entities, { jobId: null, name }, anchorName));
  };

  const remove = (index: number) => {
    onChange(entities.filter((_, i) => i !== index));
  };

  return (
    <div className={cn("grid gap-1.5", className)}>
      <p className="flex items-center gap-1.5 text-xs font-medium">
        <Building2 className="size-3.5 text-muted-foreground" />
        Entities covered ({entities.length + 1})
      </p>
      <ul className="flex min-w-0 flex-wrap gap-1.5">
        <li className="flex max-w-full items-center gap-1 rounded-md border bg-background px-1.5 py-0.5 text-xs">
          <span className="min-w-0 truncate">{anchorName}</span>
          <span className="shrink-0 text-[10px] text-muted-foreground">
            this job
          </span>
        </li>
        {entities.map((entity, index) => (
          <li
            key={entity.jobId ? String(entity.jobId) : `name:${entity.name}`}
            className="flex max-w-full items-center gap-1 rounded-md border bg-background px-1.5 py-0.5 text-xs"
          >
            <span className="min-w-0 truncate">{entity.name}</span>
            {entity.context && (
              <span
                className="shrink-0 truncate text-[10px] text-muted-foreground"
                title={`Job ${entity.context}`}
              >
                {entity.context}
              </span>
            )}
            {entity.jobId === null && (
              <span
                className="shrink-0 text-[10px] text-muted-foreground"
                title="Added by name — no job on the platform, so no searches feed the brief for it"
              >
                by name
              </span>
            )}
            <button
              type="button"
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={`Remove ${entity.name}`}
              onClick={() => remove(index)}
            >
              <X className="size-3" />
            </button>
          </li>
        ))}
      </ul>
      <SearchableSelect
        value=""
        onValueChange={addJob}
        options={options}
        placeholder="Add a related entity…"
        searchPlaceholder="Search the pipeline or type a name…"
        emptyMessage="No other jobs on the pipeline."
        onCreateOption={addName}
        createOptionLabel={(input) => `Add "${input}" by name`}
        aria-label="Add a related entity"
        className="w-full"
      />
      <p className="text-xs text-muted-foreground">
        Add the other companies in the group. Pick a job on the pipeline to
        include its searches in the brief, or type a name for one that isn't on
        the platform yet.
      </p>
    </div>
  );
}
